import PropTypes from 'prop-types';
import { STAT_CARD_BASE } from '../../ui/StatCard';
import { EditorHeading, Field, LinesInput, TextArea, TextInput, inputCls } from './EditorFields';

const MINUTE_CHOICES = [2, 3, 5, 8, 10];

/**
 * Title, what the simulation teaches, and how it runs in the room.
 *
 * Timings are stored per round (timeboxSec / negotiateSec) but edited once here and
 * applied to every round — no trainer has asked for a different clock per round, and a
 * per-round setting is one more thing to get inconsistent.
 */
const OverviewEditor = ({ meta, play, rounds, onMeta, onPlay, onRounds }) => {
  const decideMin = Math.round((rounds[0]?.timeboxSec || 300) / 60);
  const negotiateMin = Math.round((rounds[0]?.negotiateSec || 180) / 60);
  const withChoice = (choices, current) => (choices.includes(current) ? choices : [...choices, current].sort((a, b) => a - b));

  const setMeta = (field, value) => onMeta({ ...meta, [field]: value });
  const setAllRounds = (field, minutes) => onRounds(rounds.map((r) => ({ ...r, [field]: minutes * 60 })));

  return (
    <div className="space-y-4">
      <div className={`${STAT_CARD_BASE} space-y-3 p-5`}>
        <EditorHeading title="What this teaches" />
        <Field label="Title">
          <TextInput value={meta.title} onChange={(v) => setMeta('title', v)} maxLength={120} />
        </Field>
        <Field label="Subtitle">
          <TextInput value={meta.subtitle} onChange={(v) => setMeta('subtitle', v)} />
        </Field>
        <Field label="The core tension" hint="One or two sentences on the conflict at the heart of it.">
          <TextArea value={meta.coreTension} onChange={(v) => setMeta('coreTension', v)} rows={3} />
        </Field>
        <Field label="Learning objectives" hint="One per line.">
          <LinesInput value={meta.learningObjectives} onChange={(v) => setMeta('learningObjectives', v)} rows={3} />
        </Field>
      </div>

      <div className={`${STAT_CARD_BASE} space-y-3 p-5`}>
        <EditorHeading title="How it runs">
          Countdowns show on the room screen and on every phone. They never move the room on by
          themselves — you advance when you are ready, and can add a minute from the console.
        </EditorHeading>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl bg-neutral-50 p-3 dark:bg-neutral-800/60">
          <input
            type="checkbox"
            checked={play.negotiateEnabled !== false}
            onChange={(e) => onPlay({ ...play, negotiateEnabled: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-400"
          />
          <span className="text-xs leading-relaxed text-neutral-600 dark:text-neutral-300">
            <span className="font-semibold text-neutral-800 dark:text-neutral-100">Negotiation before each decision.</span>{' '}
            Teams go and talk to each other face to face before anyone commits.
          </span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Negotiating time per round">
            <select
              value={negotiateMin}
              onChange={(e) => setAllRounds('negotiateSec', Number(e.target.value))}
              disabled={play.negotiateEnabled === false}
              className={inputCls}
            >
              {withChoice(MINUTE_CHOICES, negotiateMin).map((n) => <option key={n} value={n}>{n} minutes</option>)}
            </select>
          </Field>
          <Field label="Deciding time per round">
            <select
              value={decideMin}
              onChange={(e) => setAllRounds('timeboxSec', Number(e.target.value))}
              className={inputCls}
            >
              {withChoice(MINUTE_CHOICES, decideMin).map((n) => <option key={n} value={n}>{n} minutes</option>)}
            </select>
          </Field>
        </div>
      </div>
    </div>
  );
};

OverviewEditor.propTypes = {
  meta: PropTypes.object.isRequired,
  play: PropTypes.object.isRequired,
  rounds: PropTypes.arrayOf(PropTypes.object).isRequired,
  onMeta: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  onRounds: PropTypes.func.isRequired,
};

export default OverviewEditor;
